import React, { Component } from 'react';
import YouTube from 'react-youtube';
import { serverApi } from '../api';
import './videoplayer.css';

// Extensión → tipo de medio
const EXT_IMAGEN = /\.(png|jpe?g|webp|gif|svg|avif|bmp)(\?.*)?$/i;
const EXT_VIDEO = /\.(mp4|webm|ogg|ogv|mov|m4v|mkv)(\?.*)?$/i;
const REGEX_YT_ID = /^[\w-]{11}$/;

// Acepta tanto un ID de YouTube como una URL completa (youtu.be, watch?v=, embed, shorts)
const extraerYoutubeId = (valor) => {
    if (!valor) return '';
    const v = String(valor).trim();
    if (REGEX_YT_ID.test(v)) return v;
    const m = v.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{11})/);
    return m ? m[1] : v;
};

// Resuelve rutas relativas de archivos subidos (/videos/archivo.mp4) contra el
// host del API. Las URLs absolutas y data: se usan tal cual.
const urlMedia = (ruta) => {
    if (!ruta) return '';
    if (/^https?:\/\//i.test(ruta) || ruta.startsWith('data:')) return ruta;
    const base = (serverApi.defaults.baseURL || '').replace('/api', '');
    return `${base}${ruta}`;
};

// Determina el tipo de medio de un valor suelto (compatibilidad con el string
// legado de IDs de YouTube separados por coma).
const detectarTipo = (valor) => {
    const v = String(valor || '').trim();
    if (!v) return 'youtube';
    if (EXT_IMAGEN.test(v)) return 'imagen';
    if (EXT_VIDEO.test(v)) return 'video';
    if (/^https?:\/\//i.test(v)) return 'video';
    return 'youtube';
};

// Normaliza lo que llega por props a una lista de items homogéneos:
//   { tipoMedia: 'youtube' | 'video' | 'imagen', valor, duracionSeg, titulo }
// Acepta: JSON string de items nuevos, array de items o el string legado de
// códigos de YouTube separados por coma.
const normalizarItems = (videosRaw) => {
    if (!videosRaw) return [];

    let lista;
    if (Array.isArray(videosRaw)) {
        lista = videosRaw;
    } else {
        const texto = String(videosRaw).trim();
        if (!texto) return [];
        if (texto.startsWith('[')) {
            try {
                lista = JSON.parse(texto);
            } catch (e) {
                console.warn('VideoPlayer: JSON inválido, usando separación por coma', e);
                lista = texto.split(',');
            }
        } else {
            lista = texto.split(',');
        }
    }

    return (lista || [])
        .map((item) => {
            if (item && typeof item === 'object') {
                const valor = item.valor ?? item.videoCodigo ?? item.code ?? '';
                const tipoMedia = item.tipoMedia || detectarTipo(valor);
                return {
                    tipoMedia,
                    valor,
                    duracionSeg: Number(item.duracionSeg) > 0 ? Number(item.duracionSeg) : 10,
                    titulo: item.titulo ?? item.videoTitulo ?? '',
                };
            }

            const valor = String(item || '').trim();
            return { tipoMedia: detectarTipo(valor), valor, duracionSeg: 10, titulo: '' };
        })
        .filter((it) => it.valor);
};

class VideoPlayer extends Component {

    constructor(props) {
        super(props);

        this.timerImagen = null;

        this.state = {
            windowWidth: window.innerWidth,
            videos: normalizarItems(props.videos),
            currentVideo: 0,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.programarTimerImagen();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.videos !== this.props.videos) {
            this.setState(
                {
                    videos: normalizarItems(this.props.videos),
                    currentVideo: 0,
                },
                () => this.programarTimerImagen()
            );
            return;
        }

        if (prevState.currentVideo !== this.state.currentVideo) {
            this.programarTimerImagen();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        if (this.timerImagen) {
            window.clearTimeout(this.timerImagen);
            this.timerImagen = null;
        }
    }

    handleResize = () => {
        this.setState({ windowWidth: window.innerWidth });
    };

    // Las imágenes no tienen evento de fin: se pasan a la siguiente tras
    // duracionSeg segundos.
    programarTimerImagen = () => {
        if (this.timerImagen) {
            window.clearTimeout(this.timerImagen);
            this.timerImagen = null;
        }

        const { videos, currentVideo } = this.state;
        const actual = videos[currentVideo];

        if (actual && actual.tipoMedia === 'imagen') {
            const segundos = actual.duracionSeg > 0 ? actual.duracionSeg : 10;
            this.timerImagen = window.setTimeout(() => {
                this.irSiguiente();
            }, segundos * 1000);
        }
    };

    onEnd = () => {
        this.irSiguiente();
    };

    irSiguiente = () => {
        const { currentVideo, videos } = this.state;

        if (!videos.length) return;

        const nextVideo = (currentVideo + 1) % videos.length;

        this.setState({ currentVideo: nextVideo });
    };

    handleError = () => {
        const { currentVideo, videos } = this.state;

        if (!videos.length) return;

        // Si el medio falla, pasar de inmediato al siguiente. Con un único
        // elemento no se avanza para no quedar en un bucle infinito.
        if (videos.length === 1) return;

        const nextVideo = (currentVideo + 1) % videos.length;

        this.setState({ currentVideo: nextVideo });
    };

    _onReady = (event) => {
        const player = event.target;
        this.player = player;
        player.mute(); // opcional: silenciar
        player.playVideo();
        // Forzar ocultar subtítulos / closed captions (con controls:0 no se muestran,
        // esto es solo una seguridad extra por la preferencia de sesión del navegador)
        try {
            player.setOption('captions', 'track', { languageCode: '', trackKind: 'captions' });
        } catch (e) {
            console.warn("No fue posible ocultar los subtítulos:", e);
        }
    };

    render() {
        const { windowWidth, videos, currentVideo } = this.state;

        if (!videos || videos.length === 0) {
            return (
                <div className="text-center p-4">
                    Cargando videos...
                </div>
            );
        }

        const actual = videos[currentVideo] || videos[0];

        const videoWidth = windowWidth > 400
            ? windowWidth * 0.8
            : windowWidth;

        const videoOpts = {
            height: 300,
            width: videoWidth.toString(),
            playerVars: {
                autoplay: 1,
                loop: videos.length === 1 ? 1 : 0,
                playlist: extraerYoutubeId(actual.valor),
                mute: 1,
                fs: 0,
                controls: 0,
                modestbranding: 1,
                rel: 0,
                disablekb: 1,
                iv_load_policy: 3,
                cc_load_policy: 0,
                start: 0
            }
        };

        return (
            <div>
                <div className="video-container" style={{ position: 'relative', display: 'inline-block' }}>
                    <div className="text-center video-player">
                        {actual.tipoMedia === 'youtube' && (
                            <YouTube
                                key={`yt-${currentVideo}-${actual.valor}`}
                                videoId={extraerYoutubeId(actual.valor)}
                                opts={videoOpts}
                                onEnd={this.onEnd}
                                onReady={this._onReady}
                                onError={this.handleError}
                            />
                        )}

                        {actual.tipoMedia === 'video' && (
                            <video
                                key={`vid-${currentVideo}-${actual.valor}`}
                                className="video-file"
                                src={urlMedia(actual.valor)}
                                autoPlay
                                muted
                                playsInline
                                loop={videos.length === 1}
                                onEnded={this.onEnd}
                                onError={this.handleError}
                            />
                        )}

                        {actual.tipoMedia === 'imagen' && (
                            <img
                                key={`img-${currentVideo}-${actual.valor}`}
                                className="video-image"
                                src={urlMedia(actual.valor)}
                                alt={actual.titulo || ''}
                                onError={this.handleError}
                            />
                        )}
                    </div>

                    {/* Botón flotante para pasar al siguiente medio */}
                    {videos.length > 1 && (
                        <button
                            onClick={this.irSiguiente}
                            title="Siguiente"
                            style={{
                                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                width: 46, height: 46, borderRadius: '50%', border: 'none',
                                background: 'rgba(0,0,0,0.45)', color: '#ffffff',
                                fontSize: 18, cursor: 'pointer', zIndex: 10,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                transition: 'background .2s, transform .15s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(var(--color-primario-rgb),0.75)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
                        >
                            <i className="fa-solid fa-forward-step"></i>
                        </button>
                    )}
                </div>

                <div className="d-grid gap-2 mt-2 mb-2"></div>
            </div>
        );
    }
}

export default VideoPlayer;
